<script setup lang="ts">
import { computed } from 'vue';
import type { TranslateResult } from 'vue-i18n';

import type { PredefinedPreSupportOptions } from '@/library/message/enum';
import { MessageType, PredefinedOptionType, SystemEvent } from '@/library/message/enum';
import type {
  ActionSelectedEvent,
  ArticleAction,
  BaseSystemMessage,
  PreSupportSelectionEvent,
} from '@/library/message/types';
import { randomId } from '@/library/message/lib';
import PlsText from '@/components/PlsText.vue';

import ChatMessageWrapper from './ChatMessageWrapper.vue';
import MessageActions from '../MessageActions.vue';
import { useI18n } from '@/plugins/i18n';

export interface PreSupportPromptMessageProps {
  message: BaseSystemMessage;
  /**
   * The previous message/notice in the stack.
   */
  previous?: unknown;
  /**
   * The next message/notice in the stack.
   */
  next?: unknown;
}

const props = defineProps<PreSupportPromptMessageProps>();
const emit = defineEmits<{
  (e: 'message:input', event: PreSupportSelectionEvent | ActionSelectedEvent): void;
}>();

const { t } = useI18n();

const actionItems = computed(() => {
  const { type, config } = props.message;
  const actionConfig = config?.action;

  const restartAction = {
    id: PredefinedOptionType.RESTART_CHAT,
    message: t('chat.sys.navigation.restart_here'),
    buttonText: t('chat.sys.navigation.restart_chat'),
    isBlankSpace: false,
    icon: null,
  };

  const startAgainAction = {
    id: PredefinedOptionType.RESTART_CHAT,
    message: t('chat.sys.navigation.restart_here'),
    buttonText: t('chat.sys.navigation.start_again'),
    isBlankSpace: false,
    icon: null,
  };

  const endAction = {
    id: PredefinedOptionType.END_CHAT,
    message: t('chat.sys.navigation.end_chat_here'),
    buttonText: t('global.end_chat'),
    isBlankSpace: false,
    icon: null,
  };

  const closeChatAction = {
    id: PredefinedOptionType.END_CHAT,
    message: t('chat.sys.navigation.close_chat_here'),
    buttonText: t('global.end_chat'),
    isBlankSpace: false,
    icon: null,
  };

  const contactSupportAction = {
    id: PredefinedOptionType.CONNECT_TO_SUPPORT,
    message: t('chat.sys.msg.pre_support_prompt.iff_still_issues'),
    buttonText: t('chat.sys.navigation.contact_support'),
    icon: 'MdSupportagent',
    isBlankSpace: false,
  };

  const exploreTopicsAction = {
    id: PredefinedOptionType.BROWSE_OTHER_ARTICLES,
    message: t('chat.sys.msg.pre_support_prompt.or_explore_other_topics'),
    buttonText: t('chat.sys.msg.pre_support_prompt.explore_other_topics'),
    isBlankSpace: false,
    icon: null,
  };

  const articleAction = actionConfig && {
    id: 'article-action',
    message: actionConfig.actionMessage,
    buttonText: actionConfig.actionTitle || '',
    isBlankSpace: false,
    icon: null,
  };

  const needMoreHelpAction = {
    id: PredefinedOptionType.NEED_MORE_HELP,
    message: t('chat.sys.msg.pre_support_prompt.or_need_more_help'),
    buttonText: t('chat.sys.msg.pre_support_prompt.need_more_help'),
    isBlankSpace: false,
    icon: null,
  };

  const searchArticlesAction = {
    id: PredefinedOptionType.SEARCH_ARTICLES,
    message: t('chat.sys.msg.pre_support_prompt.or_search_articles'),
    buttonText: t('chat.sys.msg.pre_support_prompt.search_articles'),
    isBlankSpace: false,
    icon: null,
  };

  const createBreak = () => ({
    id: randomId(),
    message: '',
    buttonText: '',
    icon: null,
    isBlankSpace: true,
  });

  if (type === MessageType.PRE_SUPPORT_PROMPT_ACTION_ONLY) {
    return [createBreak(), articleAction, restartAction, endAction];
  }
  if (type === MessageType.PRE_SUPPORT_PROMPT_ACTION_WITH_SUPPORT) {
    return [createBreak(), articleAction, createBreak(), contactSupportAction, exploreTopicsAction];
  }
  if (type === MessageType.PRE_SUPPORT_PROMPT_BARE) {
    return [...(props.message.message ? [createBreak()] : []), restartAction, endAction];
  }
  if (type === MessageType.PRE_SUPPORT_PROMPT_METADATA_ONLY) {
    return [createBreak(), needMoreHelpAction, restartAction, endAction];
  }
  if (type === MessageType.PRE_SUPPORT_PROMPT_FAILED_RESOLUTION) {
    return [createBreak(), startAgainAction, searchArticlesAction, closeChatAction];
  }
  if (type === MessageType.PRE_SUPPORT_PROMPT_FAILED_RESOLUTION_WITH_SUPPORT) {
    return [createBreak(), startAgainAction, searchArticlesAction, contactSupportAction, closeChatAction];
  }

  return [];
});

const buildEvent = (
  button:
    | {
        id: PredefinedPreSupportOptions | 'article-action' | string;
        message?: string | TranslateResult;
        buttonText?: string | TranslateResult;
        actionConfig?: ArticleAction;
      }
    | undefined
) => {
  if (!button) return;

  if (button.id === 'article-action') {
    if (!props.message.config?.action) return;

    const event: ActionSelectedEvent = {
      type: SystemEvent.ACTION_SELECTED,
      message: props.message,
      payload: { action: props.message.config?.action },
    };

    emit('message:input', event);

    return;
  }

  const event: PreSupportSelectionEvent = {
    type: SystemEvent.PRE_SUPPORT_SELECTION,
    message: props.message,
    payload: {
      selected: button.id as PredefinedPreSupportOptions,
    },
  };

  emit('message:input', event);
};
</script>

<template>
  <ChatMessageWrapper :previous="previous" :next="next" :message="message" actions-placement="inside-message">
    <template #actions>
      <div>
        <template v-for="btn in actionItems">
          <br v-if="btn && btn.isBlankSpace" :key="`${btn.id}-bsp`" />
          <template v-else>
            <PlsText :key="`${btn?.id}-message`">{{ btn?.message }}</PlsText>
            <div :key="`${btn?.id}-actions-wrapper`" class="action-wrapper">
              <MessageActions
                :key="`${btn?.id}-actions`"
                :items="[
                  {
                    id: btn?.id || '',
                    label: String(btn?.buttonText),
                    icon: btn?.icon || undefined,
                  },
                ]"
                @select="() => buildEvent(btn)"
              />
            </div>
          </template>
        </template>
      </div>
    </template>
  </ChatMessageWrapper>
</template>

<style scoped>
.hint-text {
  font-size: 12px;
}

:deep(.item) {
  margin-top: 8px;
}
</style>
